import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ChevronDown, GitBranch, PenLine, X } from 'lucide-react';
import type { ArchitectureEditMode } from './types';
import './architecture-edit-mode.css';

export type { ArchitectureEditMode } from './types';

export const ARCHITECTURE_EDIT_LABEL: Record<ArchitectureEditMode, string> = {
  wording: 'Wording edits',
  structural: 'Structural edits',
};

export function ArchitectureEditModeButton({ value, disabled, onClick }: {
  value: ArchitectureEditMode; disabled?: boolean; onClick: () => void;
}) {
  return <button type="button" className="arch-edit-mode-button" disabled={disabled} onClick={onClick}
    aria-label={`Architecture edit mode: ${ARCHITECTURE_EDIT_LABEL[value]}`} aria-haspopup="dialog">
    {value === 'wording' ? <PenLine size={15} aria-hidden="true"/> : <GitBranch size={15} aria-hidden="true"/>}
    <span>{ARCHITECTURE_EDIT_LABEL[value]}</span><ChevronDown size={14} aria-hidden="true"/>
  </button>;
}

export function ArchitectureEditOptions({ value, onChange, disabled = false, structuralOnly = false }: {
  value: ArchitectureEditMode; onChange: (mode: ArchitectureEditMode) => void; disabled?: boolean; structuralOnly?: boolean;
}) {
  const name = useId();
  const modes: ArchitectureEditMode[] = structuralOnly ? ['structural'] : ['wording', 'structural'];
  return <div className="arch-edit-mode-options" role="radiogroup" aria-label="Architecture edit mode">
    {modes.map(mode => <label key={mode} className={'arch-edit-mode-option' + (value === mode ? ' is-selected' : '') + (disabled ? ' is-disabled' : '')}>
      <span className="arch-edit-mode-option-icon" aria-hidden="true">{mode === 'wording' ? <PenLine size={20}/> : <GitBranch size={20}/>}</span>
      <span className="arch-edit-mode-option-copy"><strong>{ARCHITECTURE_EDIT_LABEL[mode]}</strong><span>{mode === 'wording'
        ? 'Fix text. Keep estimates, pricing and proposal.'
        : 'Change the workflow. New estimates, pricing and proposal after approval.'}</span></span>
      <input type="radio" name={name} value={mode} checked={value === mode} disabled={disabled} onChange={() => onChange(mode)}/>
    </label>)}
  </div>;
}

export function ArchitectureEditImpact({ mode }: { mode: ArchitectureEditMode }) {
  if (mode === 'wording') return null;
  return <div className={'arch-edit-mode-impact' + (mode === 'structural' ? ' is-structural' : '')}>
    <div className="arch-edit-mode-flow" aria-label="New stages follow architecture approval">
      {['Estimate', 'Pricing', 'Proposal'].map((label, index) => <React.Fragment key={label}>
        {index ? <ArrowRight size={13} aria-hidden="true"/> : null}
        <span>{label}</span>
      </React.Fragment>)}
    </div>
    {mode === 'structural' ? <p>Previous documents stay in history.</p> : null}
  </div>;
}

/** Desktop wrapper. Mini App reuses the content in its existing Sheet stack. */
export function ArchitectureEditDialog({ open, value, onChange, onCancel, onConfirm, busy = false, structuralOnly = false,
  title = 'Edit architecture', confirmLabel, error }: {
  open: boolean; value: ArchitectureEditMode; onChange: (mode: ArchitectureEditMode) => void; onCancel: () => void;
  onConfirm: () => void; busy?: boolean; structuralOnly?: boolean; title?: string; confirmLabel?: string; error?: string | null;
}) {
  const host = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const close = useRef(onCancel); close.current = onCancel;
  const busyRef = useRef(busy); busyRef.current = busy;
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const hidden = Array.from(document.body.children).filter((node): node is HTMLElement => node instanceof HTMLElement && node !== host.current)
      .map(node => ({ node, inert: node.inert, aria: node.getAttribute('aria-hidden') }));
    hidden.forEach(({ node }) => { node.inert = true; node.setAttribute('aria-hidden', 'true'); });
    (structuralOnly ? cancel.current : dialog.current?.querySelector<HTMLInputElement>('input:checked'))?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (!busyRef.current) close.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),[tabindex="0"]') || []);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first || !last) { event.preventDefault(); dialog.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = oldOverflow;
      hidden.forEach(({ node, inert, aria }) => { node.inert = inert; if (aria === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', aria); });
      if (previous?.isConnected) {
        previous.focus({ preventScroll: true });
        // Inline Save stays disabled until the cancelled await unwinds. Retry
        // after that field unlocks, without stealing focus from a new dialog.
        if (document.activeElement !== previous) requestAnimationFrame(() => {
          if (previous.isConnected && document.activeElement === document.body) previous.focus({ preventScroll: true });
        });
      }
    };
  }, [open, structuralOnly]);
  if (!open) return null;
  return createPortal(<div ref={host} className="arch-edit-mode-host" onMouseDown={event => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
    <div ref={dialog} className="arch-edit-mode-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header><h2 id={titleId}>{title}</h2><button type="button" className="arch-edit-mode-close" disabled={busy} onClick={onCancel} aria-label="Close edit mode"><X size={19}/></button></header>
      <ArchitectureEditOptions value={value} onChange={onChange} disabled={busy || structuralOnly} structuralOnly={structuralOnly}/>
      <ArchitectureEditImpact mode={value}/>
      {error ? <p className="arch-edit-mode-error" role="alert">{error}</p> : null}
      <footer><button ref={cancel} type="button" className="arch-edit-mode-cancel" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="arch-edit-mode-confirm" disabled={busy} onClick={onConfirm}>{busy ? 'Saving…' : confirmLabel || (value === 'wording' ? 'Use wording edits' : 'Use structural edits')}<ArrowRight size={17} aria-hidden="true"/></button></footer>
    </div>
  </div>, document.body);
}
